/**
 * `ScrollViewProxy.scrollTo(_:anchor:)` (docs/ops-protocol.md, "Scrolling to
 * a view"). Swift marks the target's `styled` box with `scrollTo` in exactly
 * one pass; after laying that pass out the renderer calls `scrollToTarget`,
 * which scrolls every scrolling ancestor (`scrollview`, `list`) along its
 * axes so the target's anchor point meets the same point of the visible
 * area, or, with no anchor, as little as makes the target visible.
 *
 * Geometry comes from the layout result, whose frames are screen
 * coordinates with every scroll view at offset 0, so the wanted offset is a
 * difference of frames and independent of where the views are scrolled now
 * (and of any CSS scaling of the device frame).
 */

import type { LayoutNode, LayoutResult, Rect } from './layout';
import type { ScrollToCommand, UnitPoint } from './protocol';

/** One axis of a target and a viewport (start and length), in the same coordinates. */
export interface AxisSpan {
  start: number;
  length: number;
}

/**
 * The scroll offset on one axis that puts the target where the command
 * wants it: `anchor` (0 = start, 1 = end) aligns that fraction of the target
 * with the same fraction of the viewport; `null` keeps `current` when the
 * target is fully visible, else aligns the nearer edge (the start when the
 * target is longer than the viewport). Never negative; the browser clamps
 * the far end.
 */
export function scrollOffset(target: AxisSpan, viewport: AxisSpan, anchor: number | null, current: number): number {
  const top = target.start - viewport.start;
  let offset: number;
  if (anchor !== null) {
    offset = top - anchor * (viewport.length - target.length);
  } else if (top < current || target.length > viewport.length) {
    offset = top;
  } else if (top + target.length > current + viewport.length) {
    offset = top + target.length - viewport.length;
  } else {
    offset = current;
  }
  return Math.max(0, offset);
}

/** What `scrollToTarget` needs from the renderer. */
export interface ScrollContext {
  nodes: Map<number, LayoutNode>;
  parents: Map<number, number>;
  elements: Map<number, HTMLElement>;
  /**
   * Runs an animated scroll of `el` to the offsets itself, returning false
   * to leave it to the browser's smooth scrolling. The renderer takes the
   * snapping scroll views (`scrollTargetBehavior`): a browser animating a
   * scroll-snapped container may snap it again once the animation ends or
   * the content moves (Safari does), so they run as a finger's page does.
   */
  animateScroll?: (el: HTMLElement, left: number, top: number) => boolean;
}

function anchorOf(command: ScrollToCommand | undefined): UnitPoint | null {
  const anchor = command?.anchor;
  if (!anchor || typeof anchor.x !== 'number' || typeof anchor.y !== 'number') return null;
  return { x: anchor.x, y: anchor.y };
}

/** The axes a scrolling element scrolls on, or null for anything else. */
function scrollAxes(node: LayoutNode): { horizontal: boolean; vertical: boolean } | null {
  if (node.kind === 'list') return { horizontal: false, vertical: true };
  if (node.kind !== 'scrollview') return null;
  const axes = node.props['axes'];
  return { horizontal: axes === 'horizontal' || axes === 'both', vertical: axes !== 'horizontal' };
}

/**
 * Scroll the scrolling ancestors of element `id` to it. Each axis is
 * scrolled by the nearest ancestor that scrolls on it; the visible area of
 * a scroll view a tab bar covers ends at the bar (chrome `bottomInset`).
 * Returns how many scroll positions it set (for tests).
 */
export function scrollToTarget(
  ctx: ScrollContext,
  result: LayoutResult,
  id: number,
  command: ScrollToCommand | undefined,
  smooth: boolean,
): number {
  // A list row's box is the whole row, not its content.
  const target = result.chrome.get(id)?.['row'] ?? result.frames.get(id);
  if (!target) return 0;
  const anchor = anchorOf(command);
  // Axes the command leaves out are done before they start.
  let horizontalDone = command?.axes === 'vertical';
  let verticalDone = command?.axes === 'horizontal';
  let scrolled = 0;
  for (let at = ctx.parents.get(id); at !== undefined && at !== 0; at = ctx.parents.get(at)) {
    const node = ctx.nodes.get(at);
    const el = ctx.elements.get(at);
    const frame = result.frames.get(at);
    if (!node || !el || !frame) continue;
    const axes = scrollAxes(node);
    if (!axes) continue;
    const viewport: Rect = { ...frame };
    const covered = result.chrome.get(at)?.['bottomInset'];
    if (covered) viewport.height = Math.max(0, covered.y - frame.y);
    // Under its screen's bar the visible area starts below the bar.
    const under = result.chrome.get(at)?.['topInset'];
    if (under) {
      viewport.y += under.height;
      viewport.height = Math.max(0, viewport.height - under.height);
    }
    let left = el.scrollLeft;
    let top = el.scrollTop;
    let changed = false;
    if (axes.horizontal && !horizontalDone) {
      left = scrollOffset({ start: target.x, length: target.width }, { start: viewport.x, length: viewport.width }, anchor?.x ?? null, el.scrollLeft);
      horizontalDone = true;
      changed = true;
    }
    if (axes.vertical && !verticalDone) {
      top = scrollOffset({ start: target.y, length: target.height }, { start: viewport.y, length: viewport.height }, anchor?.y ?? null, el.scrollTop);
      verticalDone = true;
      changed = true;
    }
    if (changed) {
      if (smooth && ctx.animateScroll?.(el, left, top)) {
        // Run by the context (a snapping scroll view).
      } else if (smooth && typeof el.scrollTo === 'function') {
        el.scrollTo({ left, top, behavior: 'smooth' });
      } else {
        el.scrollLeft = left;
        el.scrollTop = top;
      }
      scrolled += 1;
    }
    if (horizontalDone && verticalDone) break;
  }
  return scrolled;
}
